import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "../../store/ToastProvider";
import { usePost } from "../../hooks/usePost";
import {
  CommonDataItem,
  CommonDataRequestType,
  MyFundsMetricsRequest,
} from "../../types/CommonDataRequestTypes";
import { PageButton } from "../../components/visuals/PageButton";
import { NavBar } from "../../components/common/NavBar";
import { SideMenu } from "../../components/common/SideMenu";
import { greyText } from "../../styles";
import { MyFundsTable } from "./MyFundsTable";
import { CustomDropdown } from "../../components/visuals/PageFilter/PageFilter";
import {
  buildIndexOptions,
  buildMyFundsDataSource,
  getDynamicColumns,
} from "./utils";
import { Filter as Option } from "../../types/PageFilterTypes";
import {
  BackgroundContainer,
  MainColumn as Column,
  ElementContainer,
  NavBarContainer,
  RowContainer,
  FlexibleContainer,
} from "../../components/containers/styledComponents";
import {
  CompassMainHeader,
  CompassParagraph,
} from "../../components/common/styledComponents";
import { MESSAGES } from "../../messages";
import { Metric } from "../../models/tableModels";
import editIcon from "../../assets/svg/edit.svg";
import plusIcon from "../../assets/svg/plus.svg";
import { ROUTES } from "../../routes/routeNames";
import { toSlug as slugify } from "../../utils/formattingUtils";
import { DELETE_COLUMN } from "./constants";

import { Status } from "../../enums/Status";
import { FileOptionTabTitle } from "../../types/fileUploadTypes";

export const MyFunds = (): JSX.Element => {
  const navigate = useNavigate();
  const {
    error,
    sendData: getData,
    data,
  } = usePost<Metric[], CommonDataItem<MyFundsMetricsRequest>>();
  const { addToast } = useToast();
  const [show, setShow] = useState<boolean>(false);
  const [selectedColumns, setSelectedColumns] = useState<Option[]>([]);
  const [myFundsData, setMyFundsData] = useState<Metric[]>([]);
  const [filteredMyFundsData, setFilteredMyFundsData] = useState<Metric[]>([]);
  const [selectedDefaultBenchmark, setSelectedDefaultBenchmark] =
    useState<Option>({ key: null, displayValue: "" });
  const benchmarkOptions: Option[] = buildIndexOptions(
    buildMyFundsDataSource(myFundsData)[1]
  );
  const portfoliosOptions: Option[] = buildIndexOptions(
    buildMyFundsDataSource(myFundsData)[0].filter((fund) => !fund.is_index)
  );

  useEffect(() => {
    getFundsData();
  }, []);

  useEffect(() => {
    setMyFundsData(data ? data : []);
    setFilteredMyFundsData(data ? data : []);
  }, [data]);

  useEffect(() => {
    if (error) {
      addToast(
        error.error_user_message || MESSAGES.GET_FUND_NAMES_ERROR,
        Status.ERROR
      );
      navigate(ROUTES.LOGIN);
    }
  }, [error]);

  const getFundsData = async (): Promise<void> => {
    const parameters: CommonDataItem<MyFundsMetricsRequest> = {
      name: CommonDataRequestType.MY_FUNDS_METRICS,
      item: { [CommonDataRequestType.MY_FUNDS_METRICS]: null },
    };
    await getData("GET_COMMON_DATA", parameters);
  };

  const getButtonContent = (text: string): JSX.Element => (
    <CompassParagraph fontSize={14}>{text}</CompassParagraph>
  );

  const handleEditClick = (): void => {
    setSelectedColumns(
      selectedColumns.some((column) => column.key === DELETE_COLUMN.key)
        ? selectedColumns.filter((column) => column.key !== DELETE_COLUMN.key)
        : [...selectedColumns, DELETE_COLUMN]
    );
  };

  const onSelectPortfolios = (options: Option[]): void => {
    if (options.length === 0) {
      setFilteredMyFundsData(myFundsData);
    } else {
      setFilteredMyFundsData(
        myFundsData.filter((fund) =>
          options.some(
            (option) => option.key === fund.portfolio_index_id || fund.is_index
          )
        )
      );
    }
  };

  return (
    <BackgroundContainer>
      <Column flex={show ? "0 0 15%" : "0 0 0%"} show={show}>
        <SideMenu show={show} setShow={setShow} />
      </Column>
      <Column flex="1" bg="#F5F5F5" overflow={true}>
        <NavBarContainer>
          <RowContainer mt={1} mb={1}>
            <NavBar address={"my-funds"} />
          </RowContainer>
          <RowContainer ai={"flex-start"} jc={"flex-start"} mt={-15}>
            <CompassMainHeader pt={1} ml={12}>
              My Funds
            </CompassMainHeader>
          </RowContainer>
          <RowContainer ai={"flex-start"} jc={"flex-start"} mt={1}>
            <CompassParagraph ml={20} color={greyText}>
              {MESSAGES.MY_PORTFOLIOS_DESCRIPTION}
            </CompassParagraph>
          </RowContainer>
          <RowContainer mb={1} ml={8} jc="space-between" ai="center">
            <ElementContainer>
              <CustomDropdown
                options={portfoliosOptions}
                placeholder={{
                  key: null,
                  displayValue: "Select portfolios",
                }}
                onSelectOptions={onSelectPortfolios}
                isMultiSelect={true}
                isLoading={myFundsData.length === 0}
              />
              <CustomDropdown
                options={benchmarkOptions}
                placeholder={{
                  key: null,
                  displayValue: "Select default benchmark",
                }}
                onSelectOption={setSelectedDefaultBenchmark}
                isLoading={myFundsData.length === 0}
              />
              <CustomDropdown
                options={getDynamicColumns(myFundsData)}
                placeholder={{ key: null, displayValue: "Select metrics" }}
                isMultiSelect={true}
                onSelectOptions={setSelectedColumns}
                isLoading={myFundsData.length === 0}
              />
            </ElementContainer>
            <ElementContainer>
              <PageButton
                content={getButtonContent("Edit")}
                image={editIcon}
                action={handleEditClick}
                isLoading={myFundsData.length === 0}
                he={true}
              />
              <FlexibleContainer mr={"28px"}>
                <PageButton
                  content={getButtonContent("Add new portfolio")}
                  image={plusIcon}
                  action={(): void =>
                    navigate(
                      `${ROUTES.ADD_FILE}/${slugify(
                        FileOptionTabTitle.PORTFOLIO
                      )}`
                    )
                  }
                  isLoading={myFundsData.length === 0}
                  he={true}
                />
              </FlexibleContainer>
            </ElementContainer>
          </RowContainer>
        </NavBarContainer>
        <RowContainer ml={20}>
          <MyFundsTable
            selectedColumns={selectedColumns}
            fundsData={filteredMyFundsData}
            selectedDefaultBenchmark={selectedDefaultBenchmark}
          />
        </RowContainer>
      </Column>
    </BackgroundContainer>
  );
};
